/**
 * Payment API schemas
 */

import { z } from 'zod';
import {
    GatewayId,
    PaymentTransactionStatus,
    PaymentTransactionLifecycleEventType,
    PaymentReconciliationStatus,
} from '@pillar-portal/database/dist/frontend';
import {
    CardType,
    MerchantPaymentRequestStatusBucket,
    PaymentRequestStatus,
    DeliveryChannel,
    DeliveryProvider,
    PaymentRail,
    GatewayTransactionType,
    GatewayTransactionDetailsType,
    PaymentFeeDefinedBy,
    PublicPaymentRequestState,
    PublicPaymentRequestErrorCode,
    PaymentTransactionConfigurationOverrideReason,
    PillarPaymentRequestType,
    PillarPaymentResponseType,
    PaymentRequestGeneratedDocumentKind,
    GuestPaymentSuccessAccountCtaKind,
    PaymentTerminalOperationAction,
} from '../payment.types';
import { CredentialType } from '../secret.types';
import type {
    TransactionDetails,
    GetOpenBatchRequest,
    GetPaymentAuditLogRequest,
    GetPaymentAuditLogSearchRequest,
    GetPaymentAuditLogResponse,
    GetSavedCardTokensRequest,
    GetSavedCardTokensResponse,
    CustomerPaymentMethodListRequest,
    CustomerPaymentMethodListItem,
    CustomerPaymentMethodListResponse,
    CustomerPaymentMethodBootstrapRequest,
    CustomerPaymentMethodBootstrapResponse,
    CustomerPaymentMethodBinLookupRequest,
    CustomerPaymentRequestDetailsRequest,
    CreateCustomerPaymentMethodRequest,
    CreateCustomerPaymentMethodResponse,
    GetDelegateTokenRequest,
    GetDelegateTokenResponse,
    BinLookupRequest,
    BinLookupResponse,
    SavedCardToken,
    GetVaultTransactionsSearchRequest,
    PaymentAuditLogEntry,
    PaymentTransactionCreateData,
    CardTokenCreateData,
    PillarPaymentRequest,
    PillarPaymentResponse,
    ProcessCardTokenPaymentRequest,
    GuestProcessCardTokenPaymentRequest,
    AuthenticatedProcessCardTokenPaymentRequest,
    GuestProcessStoredPaymentMethodPaymentRequest,
    GuestBinLookupRequest,
    UpdateCustomerPaymentMethodRequest,
    UpdateCustomerPaymentMethodResponse,
    ProcessCardTokenPaymentResponse,
    CardPaymentTerminalMenuItem,
    AccessibleCredentialInfoListItem,
    AccessibleTerminalInfoListItem,
    ListCardPaymentTerminalDevicesRequest,
    ListAccessibleCredentialsInfoRequest,
    ListAccessibleTerminalsInfoRequest,
    ListCardPaymentTerminalDevicesResponse,
    ListAccessibleCredentialsInfoResponse,
    ListAccessibleTerminalsInfoResponse,
    GetPaymentConfigurationRequest,
    PaymentConfigurationResponse,
    PaymentTransactionConfiguration,
    ProcessAchPaymentRequest,
    ProcessAchPaymentResponse,
    PaymentTransactionAmountBreakdown,
    TransactionDetailsCreateRequest,
    RecordTokenRequest,
    RecordTokenResponse,
    CreditCardPaymentMethodTokenDetails,
    DebitCardPaymentMethodTokenDetails,
    AchPaymentMethodTokenDetails,
    PaymentMethodTokenDetails,
    PaymentRequestSourceDocumentKey,
    CustomerDetails,
    PaymentRequestDocumentDescriptor,
    DocumentTemplateDescriptor,
    UploadPaymentRequestDocumentResponse,
    GeneratePaymentRequestDocumentRequest,
    GeneratePaymentRequestDocumentResponse,
    CreditCardReceiptTemplateSelection,
    InvoiceTemplateSelection,
    PaymentRequestDocumentTemplateSelection,
    DocumentGenerationMerchantBranding,
    DocumentGenerationPaymentRequestSnapshot,
    CreditCardReceiptDocumentHydrationContext,
    DealertrackPartsInvoiceDocumentHydrationContext,
    DealertrackServiceInvoiceDocumentHydrationContext,
    DealertrackDealInvoiceDocumentHydrationContext,
    EInvoiceEmailHydrationContext,
    InvoiceDocumentHydrationContext,
    PaymentRequestDocumentHydrationContext,
    PaymentRequestDocumentReference,
    CreateDraftPaymentRequestRequest,
    CreateDraftPaymentRequestResponse,
    PaymentRequestOwnerCandidate,
    PaymentRequestOwnerCandidatesResponse,
    FinalizeSendPaymentRequestEmailOnlyRequest,
    FinalizeSendPaymentRequestSmsOnlyRequest,
    FinalizeSendPaymentRequestEmailAndSmsRequest,
    FinalizeSendPaymentRequestRequest,
    ResendPaymentRequestEmailOnlyRequest,
    ResendPaymentRequestSmsOnlyRequest,
    ResendPaymentRequestEmailAndSmsRequest,
    ResendPaymentRequestRequest,
    CancelAndReissuePaymentRequestEmailOnlyRequest,
    CancelAndReissuePaymentRequestSmsOnlyRequest,
    CancelAndReissuePaymentRequestEmailAndSmsRequest,
    CancelAndReissuePaymentRequestRequest,
    FinalizeSendPaymentRequestChannelDispatchResult,
    FinalizeSendPaymentRequestResponse,
    CancelAndReissuePaymentRequestResponse,
    PublicPaymentLinkActionResponse,
    PublicPaymentRequestDocumentView,
    PublicActivePaymentRequestView,
    PublicCanceledPaymentRequestView,
    PublicExpiredPaymentRequestView,
    PublicPaidPaymentRequestView,
    PublicReissuedPaymentRequestView,
    PublicErroredPaymentRequestView,
    PublicPaymentRequestView,
    GuestPaymentEntryContextView,
    GuestPaymentEntryLoginCtaView,
    AuthenticatedPaymentEntryContextView,
    GuestPaymentSuccessAccountCtaView,
    GuestPaymentSuccessView,
    PaymentRequestDeliveryEventView,
    PaymentRequestDeliveryEventList,
    PaymentRequestDeliveryEventDetailsView,
    PaymentRequestListItem,
    PaymentRequestList,
    MerchantPaymentRequestStatusBucket as MerchantPaymentRequestStatusBucketType,
    MerchantPaymentRequestSearchRequest,
    MerchantPaymentRequestListResponse,
    PaymentRequestDetailsView,
    CustomerPortalDashboardRequest,
    CustomerPortalDashboardMerchantOption,
    CustomerPortalDashboardSummary,
    CustomerPortalDashboardResponse,
    CustomerPaymentRequestListRequest,
    CustomerPaymentRequestSearchRequest,
    CustomerPaymentRequestListResponse,
    CustomerPaymentHistoryRequest,
    CustomerPaymentHistoryListItem,
    CustomerPaymentHistoryResponse,
    RecordReconciliationRequest,
    RecordReconciliationResponse,
    RecordPaymentRequestReconciliationRequest,
    RecordPaymentRequestReconciliationResponse,
    ReconcilePaymentsCandidate,
    GetReconcilePaymentsCandidatesResponse,
    ValorCardTokenizationResponse,
    CoreGatewayTransactionDetails,
    ValorGatewayTransactionCardMetadata,
    ValorGatewayTransactionTaxComponent,
    ValorGatewayTransactionTaxDetails,
    ValorGatewayTransactionDetails,
    GatewayTransactionDetailsFetchResult,
} from '../payment.types';
import {
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
    DealertrackDealTransactionDetailsV1Schema,
} from './ipw/dealertrack/dealertrack-transaction-details.schemas';

import { CursorPaginationSchema, OpaqueCursorPaginationSchema } from '../authorization.types';
import { BusinessDetailsSchema } from '../backend_config.types';
import { MerchantTimeZoneSchema } from '../timezone.utils';
import {
    JsonValueSchema,
    NonEmptyStringSchema,
    PositiveIntSchema,
    PhoneNumberSchema,
} from './common.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';
import {
    GatewayIdSchema,
    NonCoreThirdPartySystemIdSchema,
} from './third-party-system.schemas';
import { TransactionDetailsTypeId } from '../types/transaction-details.enums';
import { StoredMaskedPanSchema } from './masked_pan.schemas'

export const CardTypeSchema = z.union([z.literal(CardType.DEBIT), z.literal(CardType.CREDIT)]);
export const PaymentRailSchema = z.enum(PaymentRail);
export const PaymentRequestStatusSchema = z.enum(PaymentRequestStatus);
export const DeliveryChannelSchema = z.enum(DeliveryChannel);
export const DeliveryProviderSchema = z.enum(DeliveryProvider);
const UuidSchema = z.string().uuid();

export const CreditCardPaymentMethodTokenDetailsSchema = z.object({
    payment_rail: z.literal(PaymentRail.CREDIT_CARD),
    masked_pan: StoredMaskedPanSchema,
    card_type: z.literal(CardType.CREDIT),
    card_brand: NonEmptyStringSchema,
    cardholder_name: NonEmptyStringSchema,
    expiry_date: z.string().regex(/^\d{2}\/\d{2}$/).optional(),
}) satisfies z.ZodType<CreditCardPaymentMethodTokenDetails>;

export const DebitCardPaymentMethodTokenDetailsSchema = z.object({
    payment_rail: z.literal(PaymentRail.DEBIT_CARD),
    masked_pan: StoredMaskedPanSchema,
    card_type: z.literal(CardType.DEBIT),
    card_brand: NonEmptyStringSchema,
    cardholder_name: NonEmptyStringSchema,
    expiry_date: z.string().regex(/^\d{2}\/\d{2}$/).optional(),
}) satisfies z.ZodType<DebitCardPaymentMethodTokenDetails>;

export const AchPaymentMethodTokenDetailsSchema = z.object({
        payment_rail: z.literal(PaymentRail.ACH),
        account_holder_name: NonEmptyStringSchema,
        masked_account_number: NonEmptyStringSchema,
        masked_routing_number: NonEmptyStringSchema,
        bank_name: NonEmptyStringSchema.optional(),
    }) satisfies z.ZodType<AchPaymentMethodTokenDetails>;

export const PaymentMethodTokenDetailsSchema = z.discriminatedUnion('payment_rail', [
        CreditCardPaymentMethodTokenDetailsSchema,
        DebitCardPaymentMethodTokenDetailsSchema,
        AchPaymentMethodTokenDetailsSchema,
    ]) satisfies z.ZodType<PaymentMethodTokenDetails>;

const DealertrackPartsPaymentRequestSourceDocumentKeySchema =
    DealertrackPartsTransactionDetailsV1Schema.pick({
    transaction_details_type_id: true,
    transaction_details_type_version: true,
    partsInvoiceNumber: true,
}) satisfies z.ZodType<Extract<PaymentRequestSourceDocumentKey, { transaction_details_type_id: unknown }>>;

const DealertrackServicePaymentRequestSourceDocumentKeySchema =
    DealertrackServiceTransactionDetailsV1Schema.pick({
    transaction_details_type_id: true,
    transaction_details_type_version: true,
    roNumber: true,
}) satisfies z.ZodType<Extract<PaymentRequestSourceDocumentKey, { transaction_details_type_id: unknown }>>;

const DealertrackDealPaymentRequestSourceDocumentKeySchema =
    DealertrackDealTransactionDetailsV1Schema.pick({
    transaction_details_type_id: true,
    transaction_details_type_version: true,
    stockNumber: true,
}) satisfies z.ZodType<Extract<PaymentRequestSourceDocumentKey, { transaction_details_type_id: unknown }>>;

export const PaymentRequestSourceDocumentKeySchema = z.discriminatedUnion(
    'transaction_details_type_id',
    [
        DealertrackPartsPaymentRequestSourceDocumentKeySchema,
        DealertrackServicePaymentRequestSourceDocumentKeySchema,
        DealertrackDealPaymentRequestSourceDocumentKeySchema,
    ]
) satisfies z.ZodType<PaymentRequestSourceDocumentKey>;

export const PaymentTransactionConfigurationOverrideReasonSchema = z.enum(
    PaymentTransactionConfigurationOverrideReason
);

export const PaymentTransactionConfigurationSchema: z.ZodType<PaymentTransactionConfiguration> =
    z.object({
        credential_id: PositiveIntSchema,
        credential_type: z.enum([
            CredentialType.VIRTUAL_TERMINAL,
            CredentialType.PHYSICAL_TERMINAL,
        ]),
        is_virtual: z.boolean().nullable(),
        allow_surcharge: z.boolean(),
        allow_convenience_fee: z.boolean(),
        allow_override_fee: z.boolean(),
        surcharge_pct: z.number().nullable(),
        convenience_fee: z.number().nullable(),
        processor_tid: NonEmptyStringSchema.optional(),
        transaction_configuration_override: z.boolean().optional(),
        transaction_configuration_override_reason:
            PaymentTransactionConfigurationOverrideReasonSchema.optional(),
    });

export const GatewayTransactionDetailsTypeSchema = z.enum(GatewayTransactionDetailsType);

export const IpwTransactionDetailsSchema: z.ZodType<TransactionDetails> = z.union([
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
    DealertrackDealTransactionDetailsV1Schema,
]);

export const TransactionDetailsSchema: z.ZodType<TransactionDetails> = IpwTransactionDetailsSchema;
export const CustomerDetailsSchema: z.ZodType<CustomerDetails> = z.object({
    customerName: z.string().optional(),
    mainPhone: PhoneNumberSchema.optional(),
})


const TransactionDetailsCreateRequestSchema: z.ZodType<
    TransactionDetailsCreateRequest<TransactionDetails>
> = z.lazy(() => TransactionDetailsSchema);

export const PaymentTransactionCreateDataSchema: z.ZodType<PaymentTransactionCreateData> = z.object(
    {
        merchantOrganizationId: PositiveIntSchema,
        terminalDeviceid: PositiveIntSchema,
        transactionConfiguration: PaymentTransactionConfigurationSchema,
        merchant_transaction_id: z.never().optional(),
        customerId: z.union([PositiveIntSchema, z.undefined()]),
        paymentMethodTokenId: PositiveIntSchema.nullable(),
        gatewayId: GatewayIdSchema,
        gatewayTransactionType: z.enum(GatewayTransactionType),
        transactionDetails: TransactionDetailsCreateRequestSchema,
        gatewayTxnId: NonEmptyStringSchema.optional(),
        gatewayBatch: NonEmptyStringSchema.optional(),
        gatewayAuthCode: NonEmptyStringSchema.optional(),
        gatewayStan: NonEmptyStringSchema.optional(),
        gatewayRrn: NonEmptyStringSchema.optional(),
        cardBrand: NonEmptyStringSchema.optional(),
        cardType: CardTypeSchema.optional(),
        cardLast4: z.string().regex(/^\d{4}$/).optional(),
        cardholderName: NonEmptyStringSchema.optional(),
        status: z.enum(PaymentTransactionStatus).optional(),
        batchedAmount: z.number().positive().optional(),
        refundedAmount: z.number().nonnegative().optional(),
    }
);

export const CardTokenCreateDataSchema: z.ZodType<CardTokenCreateData> = z.object(
    {
        holderOrganizationId: PositiveIntSchema,
        terminalDeviceid: PositiveIntSchema,
        customerId: z.union([PositiveIntSchema, z.undefined()]),
        gatewayId: GatewayIdSchema,
        gatewayToken: NonEmptyStringSchema,
        cardBrand: NonEmptyStringSchema,
        cardType: CardTypeSchema,
        cardMaskedPan: NonEmptyStringSchema,
        cardholderName: NonEmptyStringSchema,
        customerDetails: CustomerDetailsSchema.optional(),
    }
);

/**
 * Terminal Sale Request
 */
export const TerminalSaleRequestSchema = z.object({
    terminal_device_uuid: UuidSchema,
    amount: z.string().optional(),
    description: z.string().optional(),
    customer_name: z.string().optional(),
    transaction_details: TransactionDetailsCreateRequestSchema,
    merchant_transaction_id: z.never().optional(),
});

/**
 * Terminal Sale Response
 */
export const TerminalSaleResponseSchema = z.object({
    payment_transaction_uuid: UuidSchema.optional(),
    gateway_txn_id: z.string().optional(),
    success: z.boolean(),
    error: z.string().optional(),
});

/**
 * Terminal Abort Request
 */
export const TerminalAbortRequestSchema = z.object({
    terminal_device_uuid: UuidSchema,
});

/**
 * Terminal Abort Response
 */
export const TerminalAbortResponseSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
});

/**
 * Void Transaction Request
 */
export const VoidTransactionRequestSchema = z.object({
    payment_transaction_uuid: UuidSchema,
    account_id: z.string().optional(),
});

/**
 * Void Transaction Response
 */
export const VoidTransactionResponseSchema = z.object({
    success: z.boolean(),
    voided_amount: z.string(),
    remaining_batched_amount: z.string(),
    error: z.string().optional(),
});

/**
 * Refund Transaction Request
 */
export const RefundTransactionRequestSchema = z.object({
    payment_transaction_uuid: UuidSchema,
    amount: z.string(),
    account_id: z.string().optional(),
});

/**
 * Refund Transaction Response
 */
export const RefundTransactionResponseSchema = z.object({
    success: z.boolean(),
    refunded_amount: z.string(),
    cumulative_refunded_amount: z.string(),
    error: z.string().optional(),
});

/**
 * Record Online Payment Request
 */
export const ValorCardTokenizationResponseSchema: z.ZodType<ValorCardTokenizationResponse> = z
    .object({
        error_no: z.literal('S00'),
        error_code: z.literal('S00'),
        cardToken: NonEmptyStringSchema,
        card_brand: NonEmptyStringSchema,
        card_type: NonEmptyStringSchema.optional(),
        response: z.null(),
    })
    .strict();

export const RecordTokenRequestSchema: z.ZodType<RecordTokenRequest> = z.object({
    customer_details: CustomerDetailsSchema,
    terminal_device_uuid: UuidSchema,
    card_type: CardTypeSchema,
    card_brand: NonEmptyStringSchema,
    masked_pan: StoredMaskedPanSchema,
    cardholder_name: NonEmptyStringSchema,
    gateway_response: ValorCardTokenizationResponseSchema,
});

/**
 * Record Online Payment Response
 */
export const RecordTokenResponseSchema: z.ZodType<RecordTokenResponse> = z.object({
    success: z.boolean(),
    payment_method_token_uuid: UuidSchema,
});

/**
 * Process Card Token Payment Request
 */
export const ProcessCardTokenPaymentRequestSchema: z.ZodType<ProcessCardTokenPaymentRequest> =
    z.object({
        terminal_device_uuid: UuidSchema,
        token: z.string().min(1),
        card_type: CardTypeSchema,
        card_brand: NonEmptyStringSchema,
        card_last4: z.string().regex(/^\d{4}$/),
        cardholder_name: NonEmptyStringSchema,
        amount: z.string().optional(),
        description: z.string().optional(),
        transaction_details: TransactionDetailsCreateRequestSchema,
        surcharge_amount: z.number().min(0),
        convenience_fee_amount: z.number().min(0),
        total_fee_amount: z.number().min(0),
        total_amount_paid: z.number().min(0),
        merchant_transaction_id: z.never().optional(),
    });

export const GuestProcessCardTokenPaymentRequestObjectSchema = z.object({
    token: z.string().min(1),
    pan_prefix: z.string().regex(/^\d{6,9}$/),
    card_brand: NonEmptyStringSchema,
    card_last4: z.string().regex(/^\d{4}$/),
    cardholder_name: NonEmptyStringSchema,
    surcharge_amount: z.number().min(0),
    convenience_fee_amount: z.number().min(0),
    total_fee_amount: z.number().min(0),
    total_amount_paid: z.number().min(0),
}) satisfies z.ZodType<GuestProcessCardTokenPaymentRequest>;

export const GuestProcessCardTokenPaymentRequestSchema: z.ZodType<GuestProcessCardTokenPaymentRequest> =
    GuestProcessCardTokenPaymentRequestObjectSchema;

export const GuestBinLookupRequestSchema: z.ZodType<GuestBinLookupRequest> = z.object({
    pan_prefix: z.string().regex(/^\d{6,9}$/),
});

/**
 * Process Card Token Payment Response
 */
export const ProcessCardTokenPaymentResponseSchema: z.ZodType<ProcessCardTokenPaymentResponse> =
    z.object({
        payment_transaction_uuid: UuidSchema.optional(),
        gateway_txn_id: z.string().optional(),
        success: z.boolean(),
        error: z.string().optional(),
    });

export const GetPaymentConfigurationRequestSchema: z.ZodType<GetPaymentConfigurationRequest> =
    z.object({
        terminal_device_uuid: UuidSchema,
    });

export const PaymentConfigurationResponseSchema: z.ZodType<PaymentConfigurationResponse> = z.object({
    account_id: z.string().min(1),
    credential_uuid: UuidSchema,
    credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
    is_virtual: z.boolean().nullable(),
    allow_surcharge: z.boolean(),
    allow_convenience_fee: z.boolean(),
    allow_override_fee: z.boolean(),
    surcharge_pct: z.number().nullable(),
    convenience_fee: z.number().nullable(),
});

const CoreGatewayTransactionDetailsSchemaBase = z.object({
    gateway_id: GatewayIdSchema,
    gateway_transaction_details_type: GatewayTransactionDetailsTypeSchema,
    gateway_transaction_details_version: PositiveIntSchema,
    gateway_txn_id: NonEmptyStringSchema,
    base_amount: z.number().nonnegative(),
    total_amount: z.number().nonnegative(),
    total_fee_amount: z.number().nonnegative(),
    total_tax_amount: z.number().nonnegative(),
    is_void: z.boolean(),
    is_settled: z.boolean(),
    refund_amount: z.number().nonnegative(),
    refund_count: z.number().int().nonnegative(),
    card_brand: NonEmptyStringSchema.optional(),
    card_last4: z.string().regex(/^\d{4}$/).optional(),
    cardholder_name: NonEmptyStringSchema.optional(),
    card_type: CardTypeSchema.optional(),
});

export const CoreGatewayTransactionDetailsSchema: z.ZodType<CoreGatewayTransactionDetails> =
    CoreGatewayTransactionDetailsSchemaBase;

export const ValorGatewayTransactionTaxComponentSchema: z.ZodType<ValorGatewayTransactionTaxComponent> =
    z.object({
        rate: z.number().nonnegative(),
        amount: z.number().nonnegative(),
        taxable_sales: z.number().nonnegative(),
    });

export const ValorGatewayTransactionTaxDetailsSchema: z.ZodType<ValorGatewayTransactionTaxDetails> =
    z.object({
        state_tax: ValorGatewayTransactionTaxComponentSchema.optional(),
        city_tax: ValorGatewayTransactionTaxComponentSchema.optional(),
        reduced_tax: ValorGatewayTransactionTaxComponentSchema.optional(),
    });

export const ValorGatewayTransactionCardMetadataSchema: z.ZodType<ValorGatewayTransactionCardMetadata> =
    z.object({
        card_bin: z.string().regex(/^\d{6}$/).optional(),
        card_type: CardTypeSchema.optional(),
        issuing_bank: NonEmptyStringSchema.optional(),
        country: NonEmptyStringSchema.optional(),
        card_level: NonEmptyStringSchema.optional(),
        card_category: NonEmptyStringSchema.optional(),
    });

const ValorGatewayTransactionDetailsSchemaBase = CoreGatewayTransactionDetailsSchemaBase.extend({
        gateway_id: z.literal(GatewayId.VALOR_US),
        gateway_transaction_details_type: z.literal(GatewayTransactionDetailsType.VALOR),
        gateway_transaction_details_version: z.literal(1),
        custom_fee_amount: z.number().nonnegative(),
        card_scheme: NonEmptyStringSchema.optional(),
        pan: z.string().optional(),
        rrn: NonEmptyStringSchema.optional(),
        stan_no: NonEmptyStringSchema.optional(),
        batch_no: NonEmptyStringSchema.optional(),
        auth_code: NonEmptyStringSchema.optional(),
        card_metadata: ValorGatewayTransactionCardMetadataSchema.optional(),
        tax_details: ValorGatewayTransactionTaxDetailsSchema.optional(),
    });

export const ValorGatewayTransactionDetailsSchema: z.ZodType<ValorGatewayTransactionDetails> =
    ValorGatewayTransactionDetailsSchemaBase;

export const GatewayTransactionDetailsSchema = z.discriminatedUnion(
    'gateway_transaction_details_type',
    [ValorGatewayTransactionDetailsSchemaBase]
);

export const GatewayTransactionDetailsFetchResultSchema: z.ZodType<GatewayTransactionDetailsFetchResult> =
    z.object({
        gateway_transaction_details: GatewayTransactionDetailsSchema,
        gateway_response: JsonValueSchema,
        gateway_response_type: NonEmptyStringSchema,
        gateway_endpoint_url: NonEmptyStringSchema.optional(),
    });

export const CardPaymentTerminalMenuItemSchema: z.ZodType<CardPaymentTerminalMenuItem> = z.object({
    terminal_device_uuid: UuidSchema,
    terminal_device_name: z.string().nullable(),
    logical_terminal_uuid: UuidSchema,
    merchant_account_uuid: UuidSchema,
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    processor_tid: NonEmptyStringSchema,
    processor_device_id: NonEmptyStringSchema,
    account_id: NonEmptyStringSchema,
    credential_uuid: UuidSchema,
    organization_uuid: UuidSchema,
    credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
    system_id: NonCoreThirdPartySystemIdSchema,
    credential_is_active: z.boolean(),
    credential_applies_to_children: z.boolean(),
    effective_allow_override_fee: z.boolean(),
    can_override_fee: z.boolean(),
    effective_surcharge_pct: z.number().nullable(),
    effective_surcharge_defined_by: z.enum(PaymentFeeDefinedBy),
    effective_convenience_fee: z.number().nullable(),
    effective_convenience_fee_defined_by: z.enum(PaymentFeeDefinedBy),
    display_label: NonEmptyStringSchema,
});

const PaymentTerminalOperationActionSchemaBase = z.enum(PaymentTerminalOperationAction);

export const PaymentTerminalOperationActionSchema: z.ZodType<PaymentTerminalOperationAction> =
    PaymentTerminalOperationActionSchemaBase;

const ListCardPaymentTerminalDevicesRequestSchemaBase = z.object({
    system_id: z.coerce
        .number()
        .refine(
            (value: number) => NonCoreThirdPartySystemIdSchema.safeParse(value).success,
            'Invalid third-party system id'
        )
        .transform((value: number) => NonCoreThirdPartySystemIdSchema.parse(value)),
    is_virtual: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => value === true || value === 'true'),
    action: PaymentTerminalOperationActionSchema,
    credential_type: z
        .enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL])
        .optional(),
    limit: z.coerce.number().int().positive().optional(),
    cursor: NonEmptyStringSchema.optional(),
});

export const ListCardPaymentTerminalDevicesRequestSchema: z.ZodType<ListCardPaymentTerminalDevicesRequest> =
    ListCardPaymentTerminalDevicesRequestSchemaBase;

const ListAccessibleTerminalsInfoRequestSchemaBase = z.object({
    system_id: z.coerce
        .number()
        .refine(
            (value: number) => NonCoreThirdPartySystemIdSchema.safeParse(value).success,
            'Invalid third-party system id'
        )
        .transform((value: number) => NonCoreThirdPartySystemIdSchema.parse(value)),
    is_virtual: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => value === true || value === 'true'),
    limit: z.coerce.number().int().positive().optional(),
    cursor: NonEmptyStringSchema.optional(),
});

export const ListAccessibleTerminalsInfoRequestSchema: z.ZodType<ListAccessibleTerminalsInfoRequest> =
    ListAccessibleTerminalsInfoRequestSchemaBase;

export const ListAccessibleCredentialsInfoRequestSchema: z.ZodType<ListAccessibleCredentialsInfoRequest> =
    z.object({
        system_id: z.coerce
            .number()
            .refine(
                (value: number) => NonCoreThirdPartySystemIdSchema.safeParse(value).success,
                'Invalid third-party system id'
            )
            .transform((value: number) => NonCoreThirdPartySystemIdSchema.parse(value)),
        is_virtual: z
            .union([z.boolean(), z.enum(['true', 'false'])])
            .transform((value) => value === true || value === 'true'),
        credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
        limit: z.coerce.number().int().positive().optional(),
        cursor: NonEmptyStringSchema.optional(),
    });

export const ListCardPaymentTerminalDevicesResponseSchema: z.ZodType<ListCardPaymentTerminalDevicesResponse> =
    z.object({
        terminals: z.array(CardPaymentTerminalMenuItemSchema),
        pagination: z.object({
            has_more: z.boolean(),
            next_cursor: NonEmptyStringSchema.optional(),
        }),
    });

export const AccessibleCredentialInfoListItemSchema: z.ZodType<AccessibleCredentialInfoListItem> = z.object(
    {
        credential_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid(),
        system_id: NonCoreThirdPartySystemIdSchema,
        account_id: NonEmptyStringSchema,
        credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
        public_fields: z.record(z.string(), z.unknown()),
        is_active: z.boolean(),
        applies_to_children: z.boolean(),
        linked_terminal_device_processor_device_id: z.string().nullable(),
    }
);

export const ListAccessibleCredentialsInfoResponseSchema: z.ZodType<ListAccessibleCredentialsInfoResponse> =
    z.object({
        credentials: z.array(AccessibleCredentialInfoListItemSchema),
        pagination: z.object({
            has_more: z.boolean(),
            next_cursor: NonEmptyStringSchema.optional(),
        }),
    });

export const AccessibleTerminalInfoListItemSchema: z.ZodType<AccessibleTerminalInfoListItem> = z.object(
    {
        terminal_device_uuid: UuidSchema,
        terminal_device_name: z.string().nullable(),
        logical_terminal_uuid: UuidSchema,
        merchant_account_uuid: UuidSchema,
        processor_name: NonEmptyStringSchema,
        processor_mid: NonEmptyStringSchema,
        processor_tid: NonEmptyStringSchema,
        processor_device_id: NonEmptyStringSchema,
        credential_uuid: UuidSchema.nullable(),
        terminal_device_credential_type: z
            .enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL])
            .nullable(),
        linked_credential_type: z
            .enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL])
            .nullable(),
        qa_has_credential_link: z.boolean(),
        qa_terminal_device_credential_type_set: z.boolean(),
        qa_terminal_device_credential_type_matches_linked_credential_type: z.boolean(),
        qa_terminal_virtuality_matches_linked_credential_type: z.boolean(),
        effective_organization_uuid: UuidSchema,
        effective_applies_to_children: z.boolean(),
        effective_allow_override_fee: z.boolean(),
        effective_surcharge_pct: z.number().nullable(),
        effective_surcharge_defined_by: z.enum(PaymentFeeDefinedBy),
        effective_convenience_fee: z.number().nullable(),
        effective_convenience_fee_defined_by: z.enum(PaymentFeeDefinedBy),
    }
);

export const ListAccessibleTerminalsInfoResponseSchema: z.ZodType<ListAccessibleTerminalsInfoResponse> =
    z.object({
        terminals: z.array(AccessibleTerminalInfoListItemSchema),
        pagination: z.object({
            has_more: z.boolean(),
            next_cursor: NonEmptyStringSchema.optional(),
        }),
    });

/**
 * Process ACH Payment Request
 */
export const ProcessAchPaymentRequestSchema: z.ZodType<ProcessAchPaymentRequest> = z.object({
    transaction_details: TransactionDetailsCreateRequestSchema,
    gateway_txn_id: z.string().optional(),
    gateway_request: JsonValueSchema.optional(),
    gateway_response: JsonValueSchema.optional(),
});

/**
 * Process ACH Payment Response
 */
export const ProcessAchPaymentResponseSchema: z.ZodType<ProcessAchPaymentResponse> = z.object({
    success: z.boolean(),
    payment_transaction_uuid: UuidSchema,
});

/**
 * Payment Audit Log Entry
 */
export const PaymentAuditLogEntrySchema: z.ZodType<PaymentAuditLogEntry> = z.object({
    payment_transaction_lifecycle_event_uuid: UuidSchema,
    payment_transaction_uuid: UuidSchema,
    event_type: z.enum(PaymentTransactionLifecycleEventType),
    event_start_ts: z.string(),
    event_end_ts: z.string(),
    event_ts: z.string().optional(),
    merchant_transaction_id: NonEmptyStringSchema,
    customer_name: z.string().optional(),
    gateway_txn_id: z.string().optional(),
    gateway_request: JsonValueSchema.optional(),
    gateway_response: JsonValueSchema.optional(),
    error_code: z.string().optional(),
    error_message: z.string().optional(),
    pillar_request: z.lazy(() => PillarPaymentRequestSchema).optional(),
    pillar_response: z.lazy(() => PillarPaymentResponseSchema).optional(),
    client_request: z.lazy(() => PillarPaymentRequestSchema).optional(),
});

/**
 * Get Open Batch Request
 */
export const GetOpenBatchRequestSchema: z.ZodType<GetOpenBatchRequest> = z.object({
    account_id: z.string().min(1),
});

/**
 * Get Open Batch Response
 */
export const GetOpenBatchResponseSchema = z.object({
    gateway_txn_ids: z.array(z.string()),
    batch_no: z.string().optional(),
});

/**
 * Query Transactions Request
 */
export const QueryTransactionsRequestSchema = z.object({
    merchant_transaction_id: z.string().optional(),
    date_filter: z.string().optional(),
    amount: z.string().optional(),
    account_id: z.string().min(1),
});

/**
 * Query Transactions Response
 */
export const QueryTransactionsResponseSchema = z.object({
    transactions: z.array(
        z.object({
            txnid: z.string(),
            amount: z.string().optional(),
            date: z.string().optional(),
            merchant_transaction_id: z.string().optional(),
        })
    ),
});

/**
 * Get Transaction Status Request
 */
export const GetTransactionStatusRequestSchema = z.object({
    req_txn_id: z.string().optional(),
    txn_id: z.string().optional(),
    channel_id: z.string().optional(),
    account_id: z.string().min(1),
});

/**
 * Get Transaction Status Response
 */
export const GetTransactionStatusResponseSchema = z.object({
    state: z.string().optional(),
    txn_id: z.string().optional(),
    merchant_transaction_id: z.string().optional(),
    amount: z.string().optional(),
    error_code: z.string().optional(),
    error_msg: z.string().optional(),
}); // Allow additional properties

/**
 * Pagination parameters
 */
export const PaginationParamsSchema = z.object({
    page: z.number().int().positive().optional(),
    limit: z.number().int().positive().optional(),
    cursor: z.string().optional(),
});

/**
 * Payment Audit Log Request
 */
export const GetPaymentAuditLogRequestSchema: z.ZodType<GetPaymentAuditLogRequest> =
    PaginationParamsSchema.extend({
        ipw_system_id: NonCoreThirdPartySystemIdSchema,
        search: z.string().optional(),
    });

export const GetPaymentAuditLogSearchRequestSchema: z.ZodType<GetPaymentAuditLogSearchRequest> =
    PaginationParamsSchema.extend({
        ipw_system_id: NonCoreThirdPartySystemIdSchema,
        terms: SearchTermsSchema,
    }).strict();

/**
 * Payment Audit Log Response
 */
export const GetPaymentAuditLogResponseSchema: z.ZodType<GetPaymentAuditLogResponse> = z.object({
    events: z.array(PaymentAuditLogEntrySchema),
    pagination: OpaqueCursorPaginationSchema,
});

/**
 * Get Saved Cards (from recent transactions) Request
 */
export const GetSavedCardTokensRequestSchema: z.ZodType<GetSavedCardTokensRequest> =
    z.object({
        customer_name: NonEmptyStringSchema,
        phone_number: PhoneNumberSchema,
        ipw_system_id: NonCoreThirdPartySystemIdSchema,
    });

export const SavedCardTokenSchema: z.ZodType<SavedCardToken> = z.object({
    created_ts: z.string(), // ISO date string
    masked_pan: StoredMaskedPanSchema,
    token: z.string(),
    card_type: CardTypeSchema,
    card_brand: NonEmptyStringSchema,
    card_last4: z.string().regex(/^\d{4}$/),
    cardholder_name: NonEmptyStringSchema,
});

/**
 * Get Saved Cards (from recent transactions) Response
 */
export const GetSavedCardTokensResponseSchema: z.ZodType<GetSavedCardTokensResponse> =
    z.object({
        cards: z.array(SavedCardTokenSchema),
    });

export const CustomerPaymentMethodListRequestSchema: z.ZodType<CustomerPaymentMethodListRequest> =
    z.object({
        customer_organization_uuid: UuidSchema.optional(),
    });

export const CustomerPaymentMethodListItemSchema: z.ZodType<CustomerPaymentMethodListItem> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        created_ts: z.string().datetime(),
        stored_for_future_use: z.boolean(),
        payment_method_token_details: PaymentMethodTokenDetailsSchema,
    });

export const CustomerPaymentMethodListResponseSchema: z.ZodType<CustomerPaymentMethodListResponse> =
    z.object({
        payment_methods: z.array(CustomerPaymentMethodListItemSchema),
    });

export const CustomerPaymentMethodBootstrapRequestSchema: z.ZodType<CustomerPaymentMethodBootstrapRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
    });

export const CustomerPaymentMethodBinLookupRequestSchema: z.ZodType<CustomerPaymentMethodBinLookupRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema,
        customer_organization_uuid: UuidSchema.optional(),
        pan_prefix: z.string().regex(/^\d{6,9}$/),
    });

export const CreateCustomerPaymentMethodRequestSchema: z.ZodType<CreateCustomerPaymentMethodRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema,
        customer_organization_uuid: UuidSchema.optional(),
        gateway_response: ValorCardTokenizationResponseSchema,
        card_brand: NonEmptyStringSchema,
        card_type: CardTypeSchema,
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: NonEmptyStringSchema,
    });

export const CreateCustomerPaymentMethodResponseSchema: z.ZodType<CreateCustomerPaymentMethodResponse> =
    CustomerPaymentMethodListItemSchema;

export const UpdateCustomerPaymentMethodRequestSchema: z.ZodType<UpdateCustomerPaymentMethodRequest> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        customer_organization_uuid: UuidSchema.optional(),
        stored_for_future_use: z.boolean(),
    });

export const CustomerPaymentRequestDetailsRequestSchema: z.ZodType<
    CustomerPaymentRequestDetailsRequest
> = z.object({
    customer_organization_uuid: UuidSchema.optional(),
});

export const UpdateCustomerPaymentMethodResponseSchema: z.ZodType<UpdateCustomerPaymentMethodResponse> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        stored_for_future_use: z.boolean(),
    });

export const GetDelegateTokenRequestSchema: z.ZodType<GetDelegateTokenRequest> = z.object({
    terminal_device_uuid: UuidSchema,
});

export const GetDelegateTokenResponseSchema: z.ZodType<GetDelegateTokenResponse> = z.object({
    client_token: NonEmptyStringSchema,
    validity: NonEmptyStringSchema,
    domain: NonEmptyStringSchema,
    epi: NonEmptyStringSchema,
});

export const CustomerPaymentMethodBootstrapResponseSchema: z.ZodType<CustomerPaymentMethodBootstrapResponse> =
    z.object({
        merchant_organization_uuid: UuidSchema,
        merchant_display_name: NonEmptyStringSchema,
        delegate_token: GetDelegateTokenResponseSchema,
    });

export const BinLookupRequestSchema: z.ZodType<BinLookupRequest> = z.object({
    terminal_device_uuid: UuidSchema,
    pan_prefix: z.string().regex(/^\d{6,9}$/),
});

export const BinLookupResponseSchema: z.ZodType<BinLookupResponse> = z.object({
    card_type: CardTypeSchema,
});

/**
 * Get Vault Transactions Request
 */
export const GetVaultTransactionsRequestSchema = PaginationParamsSchema.extend({
    ipw_customer_id: z.string().optional(),
    ipw_system_id: NonCoreThirdPartySystemIdSchema.optional(),
    search: z.string().optional(),
    gateway_transaction_types: z.array(z.enum(GatewayTransactionType)).min(1).optional(),
});

export const GetVaultTransactionsSearchRequestSchema: z.ZodType<GetVaultTransactionsSearchRequest> =
    PaginationParamsSchema.extend({
        ipw_customer_id: z.string().optional(),
        ipw_system_id: NonCoreThirdPartySystemIdSchema.optional(),
        terms: SearchTermsSchema,
        gateway_transaction_types: z.array(z.enum(GatewayTransactionType)).min(1).optional(),
    }).strict();

/**
 * Transaction List Item
 */
export const VaultTransactionListItemSchema = z.object({
    payment_transaction_uuid: UuidSchema,
    gateway_txn_id: z.string().nullable(),
    customer_name: z.string().min(1).nullable(),
    cardholder_name: z.string().min(1).nullable(),
    card_last4: z.string().regex(/^\d{4}$/).nullable(),
    original_amount: z.string(),
    batched_amount: z.string().nullable(),
    refunded_amount: z.string().nullable(),
    status: z.enum(PaymentTransactionStatus),
    merchant_transaction_id: NonEmptyStringSchema,
    transaction_auth_ts: z.string().nullable(), // ISO 8601 timestamp when auth status is known
    is_voidable: z.boolean(),
    is_refundable: z.boolean(),
    data_quality: z
        .object({
            severity: z.enum(['warning', 'error']),
            issues: z.array(
                z.object({
                    severity: z.enum(['warning', 'error']),
                    message: z.string().min(1),
                })
            ),
        })
        .nullable(),
});

/**
 * Get Vault Transactions Response
 */
export const GetVaultTransactionsResponseSchema = z.object({
    transactions: z.array(VaultTransactionListItemSchema),
    pagination: z.object({
        page: z.number().optional(),
        limit: z.number().optional(),
        total: z.number().optional(),
        has_more: z.boolean(),
        next_cursor: z.string().optional(),
    }),
});

/**
 * Transaction Lifecycle Event
 */
export const TransactionLifecycleEventSchema = z.object({
    payment_transaction_lifecycle_event_id: z.number(),
    event_type: z.enum(PaymentTransactionLifecycleEventType),
    event_start_ts: z.string(), // ISO 8601 timestamp
    event_end_ts: z.string(), // ISO 8601 timestamp
    event_ts: z.string().optional(), // Legacy compatibility alias
    amount: z.string().optional(),
    amounts: JsonValueSchema.optional(),
    error_code: z.string().optional(),
    error_message: z.string().optional(),
    gateway_request: JsonValueSchema.optional(),
    gateway_response: z.unknown().optional(),
    gateway_request_type: z.string().optional(),
    gateway_response_type: z.string().optional(),
    gateway_operation_mnemonic: z.string().optional(),
    gateway_endpoint_url: z.string().url().optional(),
    pillar_request: z.lazy(() => PillarPaymentRequestSchema).optional(),
    pillar_response: z.lazy(() => PillarPaymentResponseSchema).optional(),
});

export const PaymentTransactionAmountBreakdownSchema: z.ZodType<PaymentTransactionAmountBreakdown> =
    z
        .object({
            base_amount: z.number(),
            surcharge: z.number().optional(),
            convenience_fee: z.number().optional(),
            tax: z.number().optional(),
            donation: z.number().optional(),
            total_amount: z.number().optional(),
        })
        .transform((value): PaymentTransactionAmountBreakdown => ({
            ...value,
            total_amount:
                value.total_amount ??
                Number(
                    (
                        value.base_amount +
                        (value.surcharge ?? 0) +
                        (value.convenience_fee ?? 0) +
                        (value.donation ?? 0) +
                        (value.tax ?? 0)
                    ).toFixed(2)
                ),
        }));

export const PaymentRequestDocumentDescriptorSchema: z.ZodType<PaymentRequestDocumentDescriptor> =
    z.object({
        payment_request_document_uuid: UuidSchema,
        source_kind: z.union([
            z.literal('UPLOADED'),
            z.literal('GENERATED'),
        ]),
        original_filename: NonEmptyStringSchema.optional(),
        media_type: NonEmptyStringSchema,
        content_length_bytes: z.number().int().nonnegative(),
        content_sha256: NonEmptyStringSchema,
        document_template_uuid: UuidSchema.optional(),
    });

export const DocumentTemplateDescriptorSchema: z.ZodType<
    DocumentTemplateDescriptor
> = z.object({
    document_template_uuid: UuidSchema,
    merchant_organization_uuid: UuidSchema,
    name: NonEmptyStringSchema.optional(),
    payment_request_details_type_id: NonEmptyStringSchema,
    payment_request_details_type_version: z.number().int().nonnegative(),
    description: NonEmptyStringSchema.optional(),
    content_sha256: NonEmptyStringSchema,
    is_active: z.boolean(),
});

export const UploadPaymentRequestDocumentResponseSchema: z.ZodType<
    UploadPaymentRequestDocumentResponse
> = z.object({
    payment_request_document: PaymentRequestDocumentDescriptorSchema,
});

export const PaymentRequestGeneratedDocumentKindSchema = z.enum(
    PaymentRequestGeneratedDocumentKind
);

const DealertrackPartsCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_PARTS),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS }
    >
>;

const DealertrackServiceCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_SERVICE),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE }
    >
>;

const DealertrackDealCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_DEAL),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL }
    >
>;

const CreditCardReceiptTemplateSelectionSchema: z.ZodType<CreditCardReceiptTemplateSelection> =
    z.union([
        DealertrackPartsCreditCardReceiptTemplateSelectionSchema,
        DealertrackServiceCreditCardReceiptTemplateSelectionSchema,
        DealertrackDealCreditCardReceiptTemplateSelectionSchema,
    ]);

const DealertrackPartsInvoiceTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.INVOICE),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_PARTS),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<InvoiceTemplateSelection, { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS }>
>;

const DealertrackServiceInvoiceTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.INVOICE),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_SERVICE),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<InvoiceTemplateSelection, { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE }>
>;

const DealertrackDealInvoiceTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.INVOICE),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_DEAL),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<InvoiceTemplateSelection, { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL }>
>;

export const InvoiceTemplateSelectionSchema: z.ZodType<InvoiceTemplateSelection> = z.union([
    DealertrackPartsInvoiceTemplateSelectionSchema,
    DealertrackServiceInvoiceTemplateSelectionSchema,
    DealertrackDealInvoiceTemplateSelectionSchema,
]);

export const PaymentRequestDocumentTemplateSelectionSchema: z.ZodType<
    PaymentRequestDocumentTemplateSelection
> = z.union([CreditCardReceiptTemplateSelectionSchema, InvoiceTemplateSelectionSchema]);

export const GeneratePaymentRequestDocumentRequestSchema: z.ZodType<
    GeneratePaymentRequestDocumentRequest
> = z.object({
    payment_request_uuid: UuidSchema,
    template_selection: PaymentRequestDocumentTemplateSelectionSchema,
});

export const GeneratePaymentRequestDocumentResponseSchema: z.ZodType<
    GeneratePaymentRequestDocumentResponse
> = z.object({
    payment_request_document: PaymentRequestDocumentDescriptorSchema,
});

export const DocumentGenerationMerchantBrandingSchema: z.ZodType<
    DocumentGenerationMerchantBranding
> = z.object({
    legal_name: NonEmptyStringSchema.optional(),
    dba_name: NonEmptyStringSchema.optional(),
    address_lines: z.array(NonEmptyStringSchema),
    phone_number: PhoneNumberSchema.optional(),
    css_stylesheet: z.string().optional(),
    logo_asset_url: z.string().url().optional(),
    anchor_href_allowlist: z.array(z.string().url()),
});

export const DocumentGenerationPaymentRequestSnapshotSchema: z.ZodType<
    DocumentGenerationPaymentRequestSnapshot
> = z.object({
    payment_request_uuid: UuidSchema,
    title: NonEmptyStringSchema.optional(),
    source_document_reference: NonEmptyStringSchema.optional(),
    currency_code: NonEmptyStringSchema,
    original_amount: z.number().optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
    recipient_email: z.string().email().optional(),
    recipient_phone: PhoneNumberSchema.optional(),
    expires_ts: z.string().datetime().optional(),
});

export const PaymentRequestDocumentReferenceSchema: z.ZodType<PaymentRequestDocumentReference> =
    z.object({
        payment_request_document_uuid: UuidSchema,
    });

export const CreateDraftPaymentRequestRequestSchema: z.ZodType<CreateDraftPaymentRequestRequest> =
    z.object({
        created_by_app: z.union([z.literal('IPS'), z.literal('PILLAR')]),
        created_by_ux_state: NonEmptyStringSchema.optional(),
        terminal_device_uuid: UuidSchema,
        merchant_customer_record_uuid: UuidSchema.optional(),
        merchant_user_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
        customer_user_uuid: UuidSchema.optional(),
        source_ipw_system_id: NonCoreThirdPartySystemIdSchema.optional(),
        source_document_reference: NonEmptyStringSchema.optional(),
        source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
        title: NonEmptyStringSchema.optional(),
        message: NonEmptyStringSchema.optional(),
        payment_request_details: TransactionDetailsSchema.optional(),
        original_amount: z.number().optional(),
        amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
        currency_code: NonEmptyStringSchema,
        expires_ts: z.string().datetime().optional(),
        recipient_email: z.string().email().optional(),
        recipient_phone: PhoneNumberSchema.optional(),
        email_opt_in: z.boolean().optional(),
        sms_opt_in: z.boolean().optional(),
    });

export const CreateDraftPaymentRequestResponseSchema: z.ZodType<CreateDraftPaymentRequestResponse> =
    z.object({
        payment_request_uuid: UuidSchema,
        status: PaymentRequestStatusSchema,
    });

const FinalizeSendPaymentRequestRequestBaseSchema = z.object({
    payment_request_uuid: UuidSchema,
    allow_resend_existing_request: z.boolean().optional(),
});

const FinalizeSendPaymentRequestEmailOnlyRequestSchema: z.ZodType<
    FinalizeSendPaymentRequestEmailOnlyRequest
> = FinalizeSendPaymentRequestRequestBaseSchema.extend({
    send_email: z.literal(true),
    send_sms: z.literal(false),
    recipient_email: z.string().email(),
});

const FinalizeSendPaymentRequestSmsOnlyRequestSchema: z.ZodType<
    FinalizeSendPaymentRequestSmsOnlyRequest
> = FinalizeSendPaymentRequestRequestBaseSchema.extend({
    send_email: z.literal(false),
    send_sms: z.literal(true),
    recipient_phone: PhoneNumberSchema,
});

const FinalizeSendPaymentRequestEmailAndSmsRequestSchema: z.ZodType<
    FinalizeSendPaymentRequestEmailAndSmsRequest
> = FinalizeSendPaymentRequestRequestBaseSchema.extend({
    send_email: z.literal(true),
    send_sms: z.literal(true),
    recipient_email: z.string().email(),
    recipient_phone: PhoneNumberSchema,
});

export const FinalizeSendPaymentRequestRequestSchema: z.ZodType<
    FinalizeSendPaymentRequestRequest
> = z.union([
    FinalizeSendPaymentRequestEmailOnlyRequestSchema,
    FinalizeSendPaymentRequestSmsOnlyRequestSchema,
    FinalizeSendPaymentRequestEmailAndSmsRequestSchema,
]);

const ResendPaymentRequestRequestBaseSchema = z.object({
    send_email: z.boolean(),
    send_sms: z.boolean(),
});

const ResendPaymentRequestEmailOnlyRequestSchema: z.ZodType<ResendPaymentRequestEmailOnlyRequest> =
    ResendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(true),
        send_sms: z.literal(false),
        recipient_email: z.string().email(),
    });

const ResendPaymentRequestSmsOnlyRequestSchema: z.ZodType<ResendPaymentRequestSmsOnlyRequest> =
    ResendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(false),
        send_sms: z.literal(true),
        recipient_phone: PhoneNumberSchema,
    });

const ResendPaymentRequestEmailAndSmsRequestSchema: z.ZodType<
    ResendPaymentRequestEmailAndSmsRequest
> = ResendPaymentRequestRequestBaseSchema.extend({
    send_email: z.literal(true),
    send_sms: z.literal(true),
    recipient_email: z.string().email(),
    recipient_phone: PhoneNumberSchema,
});

export const ResendPaymentRequestRequestSchema: z.ZodType<ResendPaymentRequestRequest> = z.union(
    [
        ResendPaymentRequestEmailOnlyRequestSchema,
        ResendPaymentRequestSmsOnlyRequestSchema,
        ResendPaymentRequestEmailAndSmsRequestSchema,
    ]
);

const CancelAndReissuePaymentRequestRequestBaseSchema = z.object({
    send_email: z.boolean(),
    send_sms: z.boolean(),
});

const CancelAndReissuePaymentRequestEmailOnlyRequestSchema: z.ZodType<
    CancelAndReissuePaymentRequestEmailOnlyRequest
> = CancelAndReissuePaymentRequestRequestBaseSchema.extend({
    send_email: z.literal(true),
    send_sms: z.literal(false),
    recipient_email: z.string().email(),
});

const CancelAndReissuePaymentRequestSmsOnlyRequestSchema: z.ZodType<
    CancelAndReissuePaymentRequestSmsOnlyRequest
> = CancelAndReissuePaymentRequestRequestBaseSchema.extend({
    send_email: z.literal(false),
    send_sms: z.literal(true),
    recipient_phone: PhoneNumberSchema,
});

const CancelAndReissuePaymentRequestEmailAndSmsRequestSchema: z.ZodType<
    CancelAndReissuePaymentRequestEmailAndSmsRequest
> = CancelAndReissuePaymentRequestRequestBaseSchema.extend({
    send_email: z.literal(true),
    send_sms: z.literal(true),
    recipient_email: z.string().email(),
    recipient_phone: PhoneNumberSchema,
});

export const CancelAndReissuePaymentRequestRequestSchema: z.ZodType<
    CancelAndReissuePaymentRequestRequest
> = z.union([
    CancelAndReissuePaymentRequestEmailOnlyRequestSchema,
    CancelAndReissuePaymentRequestSmsOnlyRequestSchema,
    CancelAndReissuePaymentRequestEmailAndSmsRequestSchema,
]);

export const FinalizeSendPaymentRequestChannelDispatchResultSchema: z.ZodType<
    FinalizeSendPaymentRequestChannelDispatchResult
> = z.object({
    delivery_channel: DeliveryChannelSchema,
    accepted: z.boolean(),
    provider: DeliveryProviderSchema,
    provider_message_id: NonEmptyStringSchema.optional(),
    error_code: NonEmptyStringSchema.optional(),
    error_message: NonEmptyStringSchema.optional(),
});

export const FinalizeSendPaymentRequestResponseSchema: z.ZodType<
    FinalizeSendPaymentRequestResponse
> = z.object({
    payment_request_uuid: UuidSchema,
    status: PaymentRequestStatusSchema,
    single_use_auth_token_uuid: UuidSchema,
    single_use_auth_token_value: NonEmptyStringSchema,
    public_payment_url: z.string().url(),
    payment_request_reused: z.boolean(),
    channel_results: z.array(FinalizeSendPaymentRequestChannelDispatchResultSchema),
});

export const CancelAndReissuePaymentRequestResponseSchema: z.ZodType<
    CancelAndReissuePaymentRequestResponse
> = z.object({
    payment_request_uuid: UuidSchema,
    status: PaymentRequestStatusSchema,
    single_use_auth_token_uuid: UuidSchema,
    single_use_auth_token_value: NonEmptyStringSchema,
    public_payment_url: z.string().url(),
    payment_request_reused: z.boolean(),
    channel_results: z.array(FinalizeSendPaymentRequestChannelDispatchResultSchema),
    superseded_payment_request_uuid: UuidSchema,
});

export const PublicPaymentLinkActionResponseSchema: z.ZodType<
    PublicPaymentLinkActionResponse
> = z.object({
    payment_request_uuid: UuidSchema,
    channel_results: z.array(FinalizeSendPaymentRequestChannelDispatchResultSchema),
});

export const PublicPaymentRequestStateSchema = z.enum(PublicPaymentRequestState);
export const PublicPaymentRequestErrorCodeSchema = z.enum(PublicPaymentRequestErrorCode);

export const PublicPaymentRequestDocumentViewSchema = z.object({
    payment_request_document_uuid: UuidSchema,
    original_filename: NonEmptyStringSchema.optional(),
    media_type: NonEmptyStringSchema,
}) satisfies z.ZodType<PublicPaymentRequestDocumentView>;

const PublicResolvedPaymentRequestViewBaseSchema = z.object({
    payment_request_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
    title: NonEmptyStringSchema.optional(),
    source_document_reference: NonEmptyStringSchema.optional(),
    source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
    original_amount: z.number().optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
    currency_code: NonEmptyStringSchema,
    terminal_device_uuid: UuidSchema.optional(),
    documents: z.array(PublicPaymentRequestDocumentViewSchema),
});

export const PublicActivePaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.ACTIVE),
        expires_ts: z.string().datetime().optional(),
    }) satisfies z.ZodType<PublicActivePaymentRequestView>;

export const PublicExpiredPaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.EXPIRED),
        expires_ts: z.string().datetime().optional(),
    }) satisfies z.ZodType<PublicExpiredPaymentRequestView>;

export const PublicPaidPaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.PAID),
        fulfilled_ts: z.string().datetime().optional(),
    }) satisfies z.ZodType<PublicPaidPaymentRequestView>;

export const PublicCanceledPaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.CANCELED),
    }) satisfies z.ZodType<PublicCanceledPaymentRequestView>;

export const PublicReissuedPaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.REISSUED),
        replacement_payment_request_uuid: UuidSchema.optional(),
        replacement_public_payment_url: z.string().url().optional(),
        replacement_expires_ts: z.string().datetime().optional(),
    }) satisfies z.ZodType<PublicReissuedPaymentRequestView>;

export const PublicErroredPaymentRequestViewSchema = z.object({
    state: z.literal(PublicPaymentRequestState.ERROR),
    error_code: PublicPaymentRequestErrorCodeSchema,
}) satisfies z.ZodType<PublicErroredPaymentRequestView>;

export const PublicPaymentRequestViewSchema = z.discriminatedUnion('state', [
    PublicActivePaymentRequestViewSchema,
    PublicExpiredPaymentRequestViewSchema,
    PublicCanceledPaymentRequestViewSchema,
    PublicPaidPaymentRequestViewSchema,
    PublicReissuedPaymentRequestViewSchema,
    PublicErroredPaymentRequestViewSchema,
]) satisfies z.ZodType<PublicPaymentRequestView>;

export const GuestPaymentEntryContextViewSchema = z.object({
    payment_request_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
    title: NonEmptyStringSchema.optional(),
    source_document_reference: NonEmptyStringSchema.optional(),
    source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
    original_amount: z.number().optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
    currency_code: NonEmptyStringSchema,
    terminal_device_uuid: UuidSchema.optional(),
    payment_configuration: PaymentConfigurationResponseSchema,
    login_cta: z.lazy(() => GuestPaymentEntryLoginCtaViewSchema).optional(),
}) satisfies z.ZodType<GuestPaymentEntryContextView>;

export const GuestPaymentEntryLoginCtaViewSchema: z.ZodType<GuestPaymentEntryLoginCtaView> =
    z.object({
        href: NonEmptyStringSchema,
        email: z.string().email(),
    });

export const AuthenticatedPaymentEntryContextViewSchema: z.ZodType<AuthenticatedPaymentEntryContextView> =
    z.object({
        payment_request_uuid: UuidSchema,
        stored_payment_methods: z.array(CustomerPaymentMethodListItemSchema),
    });

export const GuestPaymentSuccessAccountCtaViewSchema: z.ZodType<GuestPaymentSuccessAccountCtaView> =
    z.object({
        kind: z.enum(GuestPaymentSuccessAccountCtaKind),
        href: NonEmptyStringSchema,
        email: z.string().email().optional(),
    });

export const GuestPaymentSuccessViewSchema: z.ZodType<GuestPaymentSuccessView> = z.object({
    payment_request_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
    gateway_txn_id: NonEmptyStringSchema.optional(),
    gateway_auth_code: NonEmptyStringSchema.optional(),
    receipt_html_path: NonEmptyStringSchema.optional(),
    account_cta: GuestPaymentSuccessAccountCtaViewSchema.optional(),
});

export const GuestProcessStoredPaymentMethodPaymentRequestSchema: z.ZodType<GuestProcessStoredPaymentMethodPaymentRequest> =
    z.object({
        payment_method_token_uuid: UuidSchema,
    });

export const AuthenticatedProcessCardTokenPaymentRequestSchema: z.ZodType<AuthenticatedProcessCardTokenPaymentRequest> =
    GuestProcessCardTokenPaymentRequestObjectSchema.extend({
        stored_for_future_use: z.boolean(),
    });

export const PaymentRequestDeliveryEventViewSchema = z.object({
        payment_request_delivery_event_uuid: UuidSchema,
        delivery_channel: DeliveryChannelSchema,
        delivery_provider: DeliveryProviderSchema,
        provider_event_type: NonEmptyStringSchema,
        provider_message_id: NonEmptyStringSchema.optional(),
        provider_event_ts: z.string().datetime().optional(),
        received_ts: z.string().datetime(),
    }) satisfies z.ZodType<PaymentRequestDeliveryEventView>;

export const PaymentRequestDeliveryEventDetailsViewSchema =
    PaymentRequestDeliveryEventViewSchema.extend({
    provider_payload: JsonValueSchema.optional(),
}) satisfies z.ZodType<PaymentRequestDeliveryEventDetailsView>;

export const PaymentRequestListItemSchema = z.object({
    payment_request_uuid: UuidSchema,
    merchant_organization_uuid: UuidSchema,
    customer_organization_uuid: UuidSchema.optional(),
    merchant_customer_record_uuid: UuidSchema.optional(),
    status: PaymentRequestStatusSchema,
    reconciled: z.enum(PaymentReconciliationStatus).nullable(),
    customer_name: NonEmptyStringSchema.optional(),
    title: NonEmptyStringSchema.optional(),
    recipient_email: z.string().email().optional(),
    recipient_phone: PhoneNumberSchema.optional(),
    source_document_reference: NonEmptyStringSchema.optional(),
    merchant_transaction_id: NonEmptyStringSchema.optional(),
    source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
    base_amount: z.number().optional(),
    original_amount: z.number().optional(),
    currency_code: NonEmptyStringSchema,
    created_ts: z.string().datetime(),
    expires_ts: z.string().datetime().optional(),
}) satisfies z.ZodType<PaymentRequestListItem>;

export const PaymentRequestListSchema: z.ZodType<PaymentRequestList> = z.object({
    payment_requests: z.array(PaymentRequestListItemSchema),
});

const PaymentRequestStatusQueryArraySchema = z.preprocess((value: unknown) => {
    if (value === undefined) {
        return undefined;
    }
    return Array.isArray(value) ? value : [value];
}, z.array(PaymentRequestStatusSchema).optional());

export const MerchantPaymentRequestStatusBucketSchema: z.ZodType<MerchantPaymentRequestStatusBucketType> =
    z.enum(MerchantPaymentRequestStatusBucket);

export const MerchantPaymentRequestSearchRequestSchema: z.ZodType<
    MerchantPaymentRequestSearchRequest
> = z
    .object({
        terms: SearchTermsSchema,
        statuses: PaymentRequestStatusQueryArraySchema,
        status_bucket: MerchantPaymentRequestStatusBucketSchema.optional(),
        require_reconcile_candidates: z.boolean().optional(),
        limit: z.coerce
            .number()
            .int()
            .positive()
            .optional()
            .transform((value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return Math.min(value, 1000);
            }),
        cursor: PositiveIntSchema.optional(),
    })
    .strict();

export const MerchantPaymentRequestListResponseSchema: z.ZodType<
    MerchantPaymentRequestListResponse
> = z.object({
    payment_requests: z.array(PaymentRequestListItemSchema),
    pagination: CursorPaginationSchema,
});

export const PaymentRequestDetailsViewSchema = PaymentRequestListItemSchema.extend({
    message: NonEmptyStringSchema.optional(),
    payment_request_details: TransactionDetailsSchema.optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
    public_payment_url: z.string().url().optional(),
    documents: z.array(PaymentRequestDocumentDescriptorSchema),
    delivery_events: z.array(PaymentRequestDeliveryEventViewSchema),
}) satisfies z.ZodType<PaymentRequestDetailsView>;

export const PaymentRequestDeliveryEventListSchema: z.ZodType<PaymentRequestDeliveryEventList> =
    z.object({
        payment_request_uuid: UuidSchema,
        delivery_events: z.array(PaymentRequestDeliveryEventViewSchema),
    });

export const PaymentRequestOwnerCandidateSchema: z.ZodType<PaymentRequestOwnerCandidate> =
    z.object({
        user_uuid: UuidSchema,
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string().email(),
    });

export const PaymentRequestOwnerCandidatesResponseSchema: z.ZodType<PaymentRequestOwnerCandidatesResponse> =
    z.object({
        current_user_uuid: UuidSchema,
        users: z.array(PaymentRequestOwnerCandidateSchema),
    });

export const CustomerPortalDashboardRequestSchema: z.ZodType<CustomerPortalDashboardRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
    });

export const CustomerPortalDashboardMerchantOptionSchema: z.ZodType<
    CustomerPortalDashboardMerchantOption
> = z.object({
    merchant_organization_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
});

export const CustomerPortalDashboardSummarySchema: z.ZodType<CustomerPortalDashboardSummary> =
    z.object({
        open_payment_request_count: z.number().int().nonnegative(),
        overdue_payment_request_count: z.number().int().nonnegative(),
        open_payment_request_total_amount: z.number().optional(),
        currency_code: NonEmptyStringSchema.optional(),
    });

export const CustomerPortalDashboardResponseSchema: z.ZodType<CustomerPortalDashboardResponse> =
    z.object({
        merchant_options: z.array(CustomerPortalDashboardMerchantOptionSchema),
        selected_merchant_organization_uuid: UuidSchema.optional(),
        summary: CustomerPortalDashboardSummarySchema,
        recent_payment_requests: z.array(PaymentRequestListItemSchema),
    });

export const CustomerPaymentRequestListRequestSchema: z.ZodType<CustomerPaymentRequestListRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
        statuses: PaymentRequestStatusQueryArraySchema,
        limit: z.coerce
            .number()
            .int()
            .positive()
            .optional()
            .transform((value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return Math.min(value, 1000);
            }),
        cursor: PositiveIntSchema.optional(),
    });

export const CustomerPaymentRequestSearchRequestSchema: z.ZodType<
    CustomerPaymentRequestSearchRequest
> = z
    .object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
        terms: SearchTermsSchema,
        statuses: PaymentRequestStatusQueryArraySchema,
        limit: z.coerce
            .number()
            .int()
            .positive()
            .optional()
            .transform((value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return Math.min(value, 1000);
            }),
        cursor: PositiveIntSchema.optional(),
    })
    .strict();

export const CustomerPaymentRequestListResponseSchema: z.ZodType<
    CustomerPaymentRequestListResponse
> = z.object({
    payment_requests: z.array(PaymentRequestListItemSchema),
    pagination: CursorPaginationSchema,
});

export const CustomerPaymentHistoryRequestSchema: z.ZodType<CustomerPaymentHistoryRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
        limit: z.coerce
            .number()
            .int()
            .positive()
            .optional()
            .transform((value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return Math.min(value, 1000);
            }),
        cursor: PositiveIntSchema.optional(),
    });

export const CustomerPaymentHistoryListItemSchema: z.ZodType<CustomerPaymentHistoryListItem> =
    z.object({
        payment_transaction_uuid: UuidSchema,
        merchant_organization_uuid: UuidSchema,
        merchant_display_name: NonEmptyStringSchema,
        payment_request_uuid: UuidSchema.optional(),
        amount: z.number(),
        currency_code: NonEmptyStringSchema,
        payment_rail: PaymentRailSchema.optional(),
        payment_method_summary: NonEmptyStringSchema.optional(),
        status: z.enum(PaymentTransactionStatus),
        submitted_ts: z.string().datetime(),
    });

export const CustomerPaymentHistoryResponseSchema: z.ZodType<CustomerPaymentHistoryResponse> =
    z.object({
        payments: z.array(CustomerPaymentHistoryListItemSchema),
        pagination: CursorPaginationSchema,
    });

/**
 * Get Transaction Details Response
 */
export const GetTransactionDetailsResponseSchema = z.object({
    payment_transaction_uuid: UuidSchema,
    merchant_organization_uuid: UuidSchema,
    merchant_organization_display_name: z.string().nullable().optional(),
    merchant_timezone: MerchantTimeZoneSchema,
    terminal_device_uuid: UuidSchema,
    processor_device_id: NonEmptyStringSchema,
    processor_tid: z.string().nullable().optional(),
    terminal_device_name: z.string().nullable().optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema,
    ipw_transaction_details: JsonValueSchema.nullable().optional(),
    gateway_id: z.number(),
    gateway_transaction_type: z.enum(GatewayTransactionType),
    gateway_txn_id: z.string().optional(),
    gateway_batch: z.string().optional(),
    gateway_auth_code: z.string().optional(),
    gateway_stan: z.string().optional(),
    gateway_rrn: z.string().optional(),
    currency_code: z.string().optional(),
    card_brand: z.string().optional(),
    card_type: CardTypeSchema.optional(),
    card_last4: z.string().optional(),
    cardholder_name: z.string().optional(),
    original_amount: z.string(),
    batched_amount: z.string().nullable(),
    refunded_amount: z.string().nullable(),
    status: z.enum(PaymentTransactionStatus),
    reconciled: z.enum(PaymentReconciliationStatus),
    ipw_system_id: z.number(),
    ipw_customer_id: z.string().nullable().optional(),
    transaction_creation_ts: z.string(),
    transaction_auth_ts: z.string().nullable().optional(),
    merchant_transaction_id: NonEmptyStringSchema,
    description: z.string().optional(),
    lifecycle_events: z.array(TransactionLifecycleEventSchema),
    gateway_request: JsonValueSchema.optional(),
    transaction_configuration: PaymentTransactionConfigurationSchema.optional(),
});

export const GetReceiptDataResponseSchema = z.object({
    merchant_transaction_id: z.string(),
    receipt_timestamp: z.string(),
    gateway_rrn: z.string().optional(),
    gateway_auth_code: z.string().optional(),
    card_brand: z.string().optional(),
    card_type: CardTypeSchema,
    card_last4: z.string().optional(),
    surcharge_pct: z.number().optional(),
    original_amount: z.string(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema,
    transactionDetails: z.lazy(() => TransactionDetailsSchema),
    business_details: BusinessDetailsSchema.optional(),
    can_get_receipt: z.boolean(),
    missing_required_fields: z.array(z.string()),
});

export const CreditCardReceiptDocumentHydrationContextSchema: z.ZodType<
    CreditCardReceiptDocumentHydrationContext
> = z.object({
    template_selection: CreditCardReceiptTemplateSelectionSchema,
    merchant_branding: DocumentGenerationMerchantBrandingSchema,
    payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
    receipt: GetReceiptDataResponseSchema,
    merchant_timezone: MerchantTimeZoneSchema,
    receipt_timestamp_display: z.string(),
});

const DealertrackPartsInvoiceDocumentHydrationContextSchema: z.ZodType<
    DealertrackPartsInvoiceDocumentHydrationContext
> = z.object({
    template_selection: DealertrackPartsInvoiceTemplateSelectionSchema,
    merchant_branding: DocumentGenerationMerchantBrandingSchema,
    payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
    payment_request_details: DealertrackPartsTransactionDetailsV1Schema,
});

const DealertrackServiceInvoiceDocumentHydrationContextSchema: z.ZodType<
    DealertrackServiceInvoiceDocumentHydrationContext
> = z.object({
    template_selection: DealertrackServiceInvoiceTemplateSelectionSchema,
    merchant_branding: DocumentGenerationMerchantBrandingSchema,
    payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
    payment_request_details: DealertrackServiceTransactionDetailsV1Schema,
});

const DealertrackDealInvoiceDocumentHydrationContextSchema: z.ZodType<
    DealertrackDealInvoiceDocumentHydrationContext
> = z.object({
    template_selection: DealertrackDealInvoiceTemplateSelectionSchema,
    merchant_branding: DocumentGenerationMerchantBrandingSchema,
    payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
    payment_request_details: DealertrackDealTransactionDetailsV1Schema,
});

export const InvoiceDocumentHydrationContextSchema: z.ZodType<
    InvoiceDocumentHydrationContext
> = z.union([
    DealertrackPartsInvoiceDocumentHydrationContextSchema,
    DealertrackServiceInvoiceDocumentHydrationContextSchema,
    DealertrackDealInvoiceDocumentHydrationContextSchema,
]);

export const PaymentRequestDocumentHydrationContextSchema: z.ZodType<
    PaymentRequestDocumentHydrationContext
> = z.union([
    CreditCardReceiptDocumentHydrationContextSchema,
    InvoiceDocumentHydrationContextSchema,
]);

export const EInvoiceEmailHydrationContextSchema: z.ZodType<
    EInvoiceEmailHydrationContext
> = z.object({
    merchant_branding: DocumentGenerationMerchantBrandingSchema,
    payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
    merchant_name: NonEmptyStringSchema,
    base_amount: z.number(),
    payment_link: z.string().url(),
    customer_name: NonEmptyStringSchema.optional(),
    formatted_source_document_reference: NonEmptyStringSchema.optional(),
});

/**
 * Record Reconciliation Request
 */
const RecordReconciliationRequestBaseSchema = z.object({
    reconciled: z.boolean(),
    gateway_request: JsonValueSchema.optional(),
    gateway_response: JsonValueSchema.optional(),
    gateway_request_type: z.string().optional(),
    gateway_response_type: z.string().optional(),
    error_code: z.string().optional(),
    error_message: z.string().optional(),
});

const RecordReconciliationRequestSingleSchema: z.ZodType<RecordReconciliationRequest> =
    RecordReconciliationRequestBaseSchema.extend({
    mode: z.literal('single'),
    payment_transaction_uuid: UuidSchema,
});

const RecordReconciliationRequestBatchSchema: z.ZodType<RecordReconciliationRequest> =
    RecordReconciliationRequestBaseSchema.extend({
    mode: z.literal('batch'),
    payment_transaction_uuids: z.array(UuidSchema).min(1),
});

export const RecordReconciliationRequestSchema: z.ZodType<RecordReconciliationRequest> = z.union([
    RecordReconciliationRequestSingleSchema,
    RecordReconciliationRequestBatchSchema,
]);

/**
 * Record Reconciliation Response
 */
export const RecordReconciliationResponseSchema: z.ZodType<RecordReconciliationResponse> = z.object({
    success: z.boolean(),
    payment_transaction_lifecycle_event_uuid: UuidSchema.optional(),
    payment_transaction_lifecycle_event_uuids: z.array(UuidSchema).optional(),
    error: z.string().optional(),
});

/**
 * Record Payment Request Reconciliation Request
 */
export const RecordPaymentRequestReconciliationRequestSchema: z.ZodType<
    RecordPaymentRequestReconciliationRequest
> = z.object({
    payment_request_uuids: z.array(UuidSchema).min(1),
    reconciled: z.boolean(),
    gateway_request: JsonValueSchema.optional(),
    gateway_response: JsonValueSchema.optional(),
    gateway_request_type: z.string().optional(),
    gateway_response_type: z.string().optional(),
    error_code: z.string().optional(),
    error_message: z.string().optional(),
});

/**
 * Record Payment Request Reconciliation Response
 */
export const RecordPaymentRequestReconciliationResponseSchema: z.ZodType<
    RecordPaymentRequestReconciliationResponse
> = z.object({
    success: z.boolean(),
    payment_transaction_lifecycle_event_uuids: z.array(UuidSchema).optional(),
    error: z.string().optional(),
});

const ReconcileCandidatesLimitSchema = z.preprocess((value) => {
    if (value === null || value === undefined || value === '') {
        return value;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
}, z.number().int().min(1).max(200));

export const GetReconcilePaymentsCandidatesRequestSchema = z.object({
    customer_name: z.string().min(1),
    ipw_system_id: NonCoreThirdPartySystemIdSchema,
    phone_number: PhoneNumberSchema.optional(),
    parts_invoice_number: z.string().min(1).optional(),
    ro_number: z.string().min(1).optional(),
    limit: ReconcileCandidatesLimitSchema.optional(),
});

export const ReconcilePaymentsCandidateSchema: z.ZodType<ReconcilePaymentsCandidate> = z.object({
    payment_transaction_uuid: UuidSchema,
    status: z.nativeEnum(PaymentTransactionStatus),
    reconciled: z.nativeEnum(PaymentReconciliationStatus),
    base_amount: z.string(),
    original_amount: z.string(),
    transaction_auth_ts: z.string(),
    merchant_transaction_id: z.string(),
    customer_name: z.string().optional(),
    gateway_txn_id: z.string().optional(),
    phone_number: PhoneNumberSchema.optional(),
    ro_number: z.string().optional(),
});

export const GetReconcilePaymentsCandidatesResponseSchema: z.ZodType<
    GetReconcilePaymentsCandidatesResponse
> = z.object({
    transactions: z.array(ReconcilePaymentsCandidateSchema),
});

const PillarTerminalSaleRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.TERMINAL_SALE),
    request: TerminalSaleRequestSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarTerminalAbortRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.TERMINAL_ABORT),
    request: TerminalAbortRequestSchema,
});

const PillarVoidTransactionRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.VOID_TRANSACTION),
    request: VoidTransactionRequestSchema,
});

const PillarRefundTransactionRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.REFUND_TRANSACTION),
    request: RefundTransactionRequestSchema,
});

const PillarAchPaymentRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.ACH_PAYMENT),
    request: ProcessAchPaymentRequestSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarRecordTokenRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.RECORD_TOKEN),
    request: RecordTokenRequestSchema,
});

const PillarProcessCardTokenPaymentRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT),
    request: ProcessCardTokenPaymentRequestSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarGetOpenBatchRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_OPEN_BATCH),
    request: GetOpenBatchRequestSchema,
});

const PillarQueryTransactionsRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.QUERY_TRANSACTIONS),
    request: QueryTransactionsRequestSchema,
});

const PillarGetTransactionStatusRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_TRANSACTION_STATUS),
    request: GetTransactionStatusRequestSchema,
});

const PillarGetVaultTransactionsRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_VAULT_TRANSACTIONS),
    request: GetVaultTransactionsRequestSchema,
});

const PillarGetTransactionDetailsRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_TRANSACTION_DETAILS),
    payment_transaction_id: PositiveIntSchema,
});

const PillarRecordReconciliationRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.RECORD_RECONCILIATION),
    request: RecordReconciliationRequestSchema,
});

const PillarPaymentAuditLogRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.PAYMENT_AUDIT_LOG),
    request: GetPaymentAuditLogRequestSchema,
});

export const PillarPaymentRequestTypeSchema: z.ZodType<PillarPaymentRequestType> = z.union([
    z.literal(PillarPaymentRequestType.TERMINAL_SALE),
    z.literal(PillarPaymentRequestType.TERMINAL_ABORT),
    z.literal(PillarPaymentRequestType.VOID_TRANSACTION),
    z.literal(PillarPaymentRequestType.REFUND_TRANSACTION),
    z.literal(PillarPaymentRequestType.ACH_PAYMENT),
    z.literal(PillarPaymentRequestType.RECORD_TOKEN),
    z.literal(PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT),
    z.literal(PillarPaymentRequestType.GET_OPEN_BATCH),
    z.literal(PillarPaymentRequestType.QUERY_TRANSACTIONS),
    z.literal(PillarPaymentRequestType.GET_TRANSACTION_STATUS),
    z.literal(PillarPaymentRequestType.GET_VAULT_TRANSACTIONS),
    z.literal(PillarPaymentRequestType.GET_TRANSACTION_DETAILS),
    z.literal(PillarPaymentRequestType.RECORD_RECONCILIATION),
    z.literal(PillarPaymentRequestType.PAYMENT_AUDIT_LOG),
]);

export const PillarPaymentRequestSchema: z.ZodType<PillarPaymentRequest> = z.discriminatedUnion(
    'request_type',
    [
        PillarTerminalSaleRequestSchema,
        PillarTerminalAbortRequestSchema,
        PillarVoidTransactionRequestSchema,
        PillarRefundTransactionRequestSchema,
        PillarAchPaymentRequestSchema,
        PillarRecordTokenRequestSchema,
        PillarProcessCardTokenPaymentRequestSchema,
        PillarGetOpenBatchRequestSchema,
        PillarQueryTransactionsRequestSchema,
        PillarGetTransactionStatusRequestSchema,
        PillarGetVaultTransactionsRequestSchema,
        PillarGetTransactionDetailsRequestSchema,
        PillarRecordReconciliationRequestSchema,
        PillarPaymentAuditLogRequestSchema,
    ]
);

const PillarTerminalSaleResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.TERMINAL_SALE),
    response: TerminalSaleResponseSchema,
});

const PillarTerminalAbortResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.TERMINAL_ABORT),
    response: TerminalAbortResponseSchema,
});

const PillarVoidTransactionResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.VOID_TRANSACTION),
    response: VoidTransactionResponseSchema,
});

const PillarRefundTransactionResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.REFUND_TRANSACTION),
    response: RefundTransactionResponseSchema,
});

const PillarAchPaymentResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.ACH_PAYMENT),
    response: ProcessAchPaymentResponseSchema,
});

const PillarRecordTokenResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.RECORD_TOKEN),
    response: RecordTokenResponseSchema,
});

const PillarProcessCardTokenPaymentResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.PROCESS_CARD_TOKEN_PAYMENT),
    response: ProcessCardTokenPaymentResponseSchema,
});

const PillarGetOpenBatchResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_OPEN_BATCH),
    response: GetOpenBatchResponseSchema,
});

const PillarQueryTransactionsResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.QUERY_TRANSACTIONS),
    response: QueryTransactionsResponseSchema,
});

const PillarGetTransactionStatusResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_TRANSACTION_STATUS),
    response: GetTransactionStatusResponseSchema,
});

const PillarGetVaultTransactionsResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_VAULT_TRANSACTIONS),
    response: GetVaultTransactionsResponseSchema,
});

const PillarGetTransactionDetailsResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_TRANSACTION_DETAILS),
    response: GetTransactionDetailsResponseSchema,
});

const PillarRecordReconciliationResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.RECORD_RECONCILIATION),
    response: RecordReconciliationResponseSchema,
});

const PillarPaymentAuditLogResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.PAYMENT_AUDIT_LOG),
    response: GetPaymentAuditLogResponseSchema,
});

export const PillarPaymentResponseSchema: z.ZodType<PillarPaymentResponse> = z.discriminatedUnion(
    'response_type',
    [
        PillarTerminalSaleResponseSchema,
        PillarTerminalAbortResponseSchema,
        PillarVoidTransactionResponseSchema,
        PillarRefundTransactionResponseSchema,
        PillarAchPaymentResponseSchema,
        PillarRecordTokenResponseSchema,
        PillarProcessCardTokenPaymentResponseSchema,
        PillarGetOpenBatchResponseSchema,
        PillarQueryTransactionsResponseSchema,
        PillarGetTransactionStatusResponseSchema,
        PillarGetVaultTransactionsResponseSchema,
        PillarGetTransactionDetailsResponseSchema,
        PillarRecordReconciliationResponseSchema,
        PillarPaymentAuditLogResponseSchema,
    ]
);

/**
 * Common param/query schemas
 */
export const TransactionIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const PaymentTransactionUuidParamSchema: z.ZodType<string> = UuidSchema;
export const AccountIdQuerySchema: z.ZodType<string> = NonEmptyStringSchema;
